import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Label } from "@/components/ui/label";
import { authService } from "@/services/auth";
import { Button } from "@/components/ui/button";
import { Shield, Loader, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  InputOTP,
  InputOTPSlot,
  InputOTPGroup,
} from "@/components/ui/input-otp";
import {
  Dialog,
  DialogTitle,
  DialogHeader,
  DialogFooter,
  DialogContent,
  DialogDescription,
} from "@/components/ui/dialog";

interface MfaVerificationDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onError?: (error: Error) => void;
  factorId: string;
  challengeId: string;
}

export function MfaVerificationDialog({
  open,
  onClose,
  onSuccess,
  onError,
  factorId,
  challengeId,
}: MfaVerificationDialogProps) {
  const [verificationCode, setVerificationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { toast } = useToast();

  // Reset error when code changes
  const handleCodeChange = (value: string) => {
    setVerificationCode(value);
    if (errorMessage) setErrorMessage("");
  };

  const handleVerify = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setErrorMessage("Please enter a valid 6-digit verification code.");
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      await authService.mfaVerify({
        factorId,
        challengeId,
        code: verificationCode,
      });

      toast({
        title: "Verification Successful",
        description: "You have successfully verified your identity.",
      });
      onSuccess();
    } catch (error) {
      console.error("Error verifying MFA code:", error);

      // Set explicit error message in the dialog
      setErrorMessage("Invalid verification code. Please try again.");

      toast({
        variant: "destructive",
        title: "Verification Failed",
        description: "Invalid verification code. Please try again.",
      });

      if (onError && error instanceof Error) {
        onError(error);
      }

      setVerificationCode("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        // If dialog is trying to close, prevent it
        if (!isOpen) {
          onClose();
          return false; // This prevents the dialog from closing
        }
        return true;
      }}
    >
      <DialogContent
        className="sm:max-w-[425px]"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Two-Factor Authentication
          </DialogTitle>
          <DialogDescription>
            Please enter the verification code from your authenticator app to
            proceed. Verification is required to access your account.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {errorMessage && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          <div className="grid gap-2">
            <Label>Verification Code</Label>
            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={verificationCode}
                onChange={handleCodeChange}
                onComplete={handleVerify}
                autoFocus
                containerClassName="gap-2"
              >
                <InputOTPGroup className="gap-1 sm:gap-2">
                  {Array.from({ length: 6 }, (_, i) => (
                    <InputOTPSlot
                      key={i}
                      index={i}
                      className="h-10 w-10 sm:h-11 sm:w-11 rounded-md border-2"
                    />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>
          </div>
        </div>

        <DialogFooter>
          {/* Cancel button removed to prevent users from canceling verification */}
          <Button
            onClick={handleVerify}
            disabled={verificationCode.length !== 6 || loading}
            className="w-full"
          >
            {loading ? (
              <>
                <Loader className="mr-2 h-4 w-4 animate-spin" />
                Verifying...
              </>
            ) : (
              "Verify"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
